import { memo } from 'react';

import {
  faBicycle,
  faCar,
  faChartPie,
  faClock,
  faExclamationTriangle,
  faUser,
  faUserSlash
} from '@fortawesome/pro-light-svg-icons';
import {
  EmployeeResponse,
  EmployeeTypeResponse,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import { useTranslation } from 'react-i18next';

import { formatSecondsToHoursMinutes } from 'utils/date';

import { Box, styled } from '@mui/material';

import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { BLUE_SECONDARY } from 'constants/colors';

const Header = styled(Box)``;

const IconsSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 0 5px;
`;

const Icon = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface Props {
  planningEntityType?: PlanningEntityType;
  productivity?: number;
  transportationMode?: TransportationMode;
  travelKilometers?: number;
  durationInSeconds?: number;
  hasDuty: boolean;
  employee?: EmployeeResponse;
  employeeType?: EmployeeTypeResponse;
  showEmployeeName?: boolean;
  showDuty?: boolean;
  showDurationDistanceTransport?: boolean;
}

const ActivitySummary = memo((props: Props) => {
  const {
    planningEntityType,
    productivity,
    transportationMode,
    travelKilometers,
    hasDuty,
    durationInSeconds,
    employee,
    showEmployeeName,
    showDuty,
    showDurationDistanceTransport
  } = props;

  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning'
  });

  const isRoute = planningEntityType === 'ROUTE';

  return (
    <>
      <Header
        display="flex"
        flexDirection="column"
        alignItems="space-between"
        sx={{
          position: 'relative',
          padding: '8px',
          paddingTop: 0,
          paddingBottom: 0,
          backgroundColor: BLUE_SECONDARY,
          // To hide the bottom corner radius of the sticky header
          ...(isRoute && {
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '-5px',
              width: '100%',
              height: '5px',
              backgroundColor: BLUE_SECONDARY
            }
          })
        }}
      >
        {showDuty && (
          <Box p="0 5px">
            {!hasDuty && (
              <Icon>
                <FontAwesomeSvgIcon
                  icon={faExclamationTriangle}
                  fontSize="inherit"
                  color="warning"
                />
                {t('noDutyAssigned')}
              </Icon>
            )}
            {hasDuty && !employee && (
              <Icon>
                <FontAwesomeSvgIcon
                  icon={faUserSlash}
                  fontSize="inherit"
                  color="warning"
                />
                {t('noEmployeeAssigned')}
              </Icon>
            )}
            {hasDuty && showEmployeeName && employee && (
              <Icon>
                <FontAwesomeSvgIcon
                  icon={faUser}
                  fontSize="inherit"
                  color="primary"
                />
                {employee.nameInformal}
              </Icon>
            )}
          </Box>
        )}
        {showDurationDistanceTransport && (
          <IconsSection mb={1}>
            {durationInSeconds !== undefined && (
              <Icon>
                <FontAwesomeSvgIcon icon={faClock} fontSize="inherit" />
                {formatSecondsToHoursMinutes(durationInSeconds)}
              </Icon>
            )}
            {travelKilometers !== undefined && (
              <Icon>
                <FontAwesomeSvgIcon
                  icon={transportationMode == 'CAR' ? faCar : faBicycle}
                  fontSize="inherit"
                />
                {travelKilometers}km
              </Icon>
            )}
            {productivity !== undefined && (
              <Icon>
                <FontAwesomeSvgIcon icon={faChartPie} fontSize="inherit" />
                {(productivity * 100).toFixed()}%
              </Icon>
            )}
          </IconsSection>
        )}
      </Header>
    </>
  );
});

export default ActivitySummary;
